import React, { useState, useEffect, useRef } from "react";
import { InputNumber, Select, Button, Switch, Divider, Input, Alert } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { DevType } from "../../../flatbuffers/generated/front-end/dev-type";
import NumatoFlatMessageFields from "./NumatoFlatMessageFields";
import {
  type NumatoFlatMessage,
  buildDefaultNumatoFlatMessage,
  readNumatoFlatMessage,
} from "../../../flatbuffers/types/numatoFlatMessage";
import {
  type U128,
  defaultU128,
  renderU128Fields,
  numatoU128Problems,
  U128_VALUE_LABEL,
} from "./numatoU128Fields";
import {
  type ControllerBoard,
  MAX_TRIGGER_RELAYS,
  MAX_BAUD_RATE,
  defaultUsbBackend,
  defaultHostBackend,
  defaultControllerBoard,
  triggerValues,
  withTriggerValues,
  triggerMax,
  nextFreeTrigger,
  readController,
  numatoRelayControllerProblems,
} from "./numatoRelayController";

// ************************************************ */
// RELAY COMMAND TYPES **************************** */
const COMMAND_CATEGORIES = [
  { label: "Info Message", value: "InfoMessage" },
  { label: "ID Command", value: "IdCommandContentTable" },
  { label: "GPIO Command", value: "GpioCommandContentTable" },
  { label: "Version Message", value: "VersionMessage" },
  { label: "Relay Command", value: "RelayCommandContentTable" },
  { label: "ADC Command", value: "AdcCommandContentTable" },
];

const ID_SUB_TYPES = [
  { label: "Get Message", value: "GetMessage" },
  { label: "Set Message", value: "SetMessage" },
];

const GPIO_SUB_TYPES = [
  { label: "Read Message", value: "ReadMessage" },
  { label: "Clear Message", value: "ClearMessage" },
  { label: "Set Message", value: "SetMessage" },
];

const RELAY_SUB_TYPES = [
  { label: "On Message", value: "OnMessage" },
  { label: "Off Message", value: "OffMessage" },
  { label: "Read Message", value: "ReadMessage" },
  { label: "Read All Message", value: "ReadAllMessage" },
  { label: "Write All Message", value: "WriteAllMessage" },
  { label: "Write All Message (wide mask)", value: "RelayWriteAllMessage" },
];

const ADC_SUB_TYPES = [{ label: "Read Message", value: "ReadMessage" }];

// ************************************************ */
// RESPONSE TYPES ********************************* */
const RESPONSE_TYPES = [
  { label: "String Response", value: "StringResponse" },
  { label: "Numeric Response", value: "NumericResponse" },
  { label: "Bool Response", value: "BoolResponse" },
  { label: "Tuple Response", value: "TupleResponse" },
  { label: "No Response", value: "NoResponse" },
];

// ************************************************ */
// DEV TYPE OPTIONS ******************************* */
const DEV_TYPE_OPTIONS = Object.entries(DevType)
  .filter(([, v]) => typeof v === "number")
  .map(([label, value]) => ({ label: `${label} (${value})`, value: value as number }));

// ************************************************ */
// BOARD / CONTROLLER TYPES AND OPTIONS ************ */
const BOARD_OPTIONS = [
  { label: "Relay board (USB serial or Ethernet, set by the channel)", value: "relay-board" },
  { label: "Relay controller board (GPIO-driven)", value: "controller-board" },
];

const TRIGGER_BACKEND_OPTIONS = [
  { label: "Numato USB GPIO board", value: "NumatoUsbGpioPins" },
  { label: "Host GPIO chip", value: "HostGpioLines" },
];

// ************************************************ */
// TYPED INTERNAL STATE *************************** */
interface RelayCommand {
  kind: string;
  content?: { kind: string; message?: number; value?: U128 };
}

interface NumatoResponse {
  kind: string;
  message?: string | number | boolean;
  val_0?: number;
  val_1?: number;
}

interface NumatoLookupValue {
  dev_type: number;
  bank_id: string;
  lift_id: string;
  timestamp: number;
  derived: boolean;
  msg: NumatoFlatMessage;
}

interface ResponseToMessageEntry {
  key: {
    command: RelayCommand;
    response: NumatoResponse;
  };
  value: NumatoLookupValue;
}

interface MessageToCmdEntry {
  key: NumatoFlatMessage;
  value: RelayCommand;
}

interface NumatoRelayCdo {
  response_to_message: ResponseToMessageEntry[];
  message_to_cmd: MessageToCmdEntry[];
  controller?: ControllerBoard;
}

// ************************************************ */
// DEFAULT BUILDERS ******************************* */
function buildDefaultCommand(category: string): RelayCommand {
  switch (category) {
    case "InfoMessage":
      return { kind: "InfoMessage" };
    case "IdCommandContentTable":
      return {
        kind: "IdCommandContentTable",
        content: { kind: "GetMessage" },
      };
    case "GpioCommandContentTable":
      return {
        kind: "GpioCommandContentTable",
        content: { kind: "ReadMessage", message: 0 },
      };
    case "VersionMessage":
      return { kind: "VersionMessage" };
    case "RelayCommandContentTable":
      return {
        kind: "RelayCommandContentTable",
        content: { kind: "ReadMessage", message: 0 },
      };
    case "AdcCommandContentTable":
      return {
        kind: "AdcCommandContentTable",
        content: { kind: "ReadMessage", message: 0 },
      };
    default:
      return { kind: "InfoMessage" };
  }
}

function buildDefaultCommandSubType(category: string, subKind: string): RelayCommand {
  if (category === "InfoMessage" || category === "VersionMessage") {
    return { kind: category };
  }
  if (category === "RelayCommandContentTable" && subKind === "RelayWriteAllMessage") {
    return { kind: category, content: { kind: subKind, value: defaultU128() } };
  }
  const needsMessage =
    !(category === "IdCommandContentTable" && subKind === "GetMessage") &&
    !(category === "RelayCommandContentTable" && subKind === "ReadAllMessage");
  return {
    kind: category,
    content: needsMessage ? { kind: subKind, message: 0 } : { kind: subKind },
  };
}

function buildDefaultResponse(kind: string): NumatoResponse {
  switch (kind) {
    case "StringResponse":
      return { kind: "StringResponse", message: "" };
    case "NumericResponse":
      return { kind: "NumericResponse", message: 0 };
    case "BoolResponse":
      return { kind: "BoolResponse", message: false };
    case "TupleResponse":
      return { kind: "TupleResponse", val_0: 0, val_1: 0 };
    case "NoResponse":
      return { kind: "NoResponse" };
    default:
      return { kind: "NoResponse" };
  }
}

function buildDefaultLookupValue(devType = 4): NumatoLookupValue {
  return {
    dev_type: devType,
    bank_id: "",
    lift_id: "",
    timestamp: 0,
    derived: false,
    msg: buildDefaultNumatoFlatMessage(),
  };
}

function buildEmptyCdo(): NumatoRelayCdo {
  return { response_to_message: [], message_to_cmd: [] };
}

function withoutController(cdo: NumatoRelayCdo): NumatoRelayCdo {
  const { controller: _controller, ...rest } = cdo;
  return rest;
}

// ************************************************ */
// ENTRY CARD STYLE ******************************* */
const entryStyle: React.CSSProperties = {
  border: "1px solid #303030",
  borderRadius: 6,
  padding: 12,
  marginBottom: 12,
  position: "relative",
};

const deleteButtonStyle: React.CSSProperties = {
  position: "absolute",
  top: 8,
  right: 8,
};

const sectionLabelStyle: React.CSSProperties = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 1,
  color: "#888",
};

// ************************************************ */
// SUB-RENDERERS ********************************** */

/** Determine if a command category has sub-types. */
function getSubTypes(category: string): { label: string; value: string }[] | null {
  switch (category) {
    case "IdCommandContentTable":
      return ID_SUB_TYPES;
    case "GpioCommandContentTable":
      return GPIO_SUB_TYPES;
    case "RelayCommandContentTable":
      return RELAY_SUB_TYPES;
    case "AdcCommandContentTable":
      return ADC_SUB_TYPES;
    default:
      return null;
  }
}

/** Whether a command sub-type needs a message (uint8) field. */
function subTypeNeedsMessage(category: string, subKind: string): boolean {
  if (category === "IdCommandContentTable" && subKind === "GetMessage") return false;
  if (category === "RelayCommandContentTable" && subKind === "ReadAllMessage") return false;
  if (category === "RelayCommandContentTable" && subKind === "RelayWriteAllMessage") return false;
  return true;
}

/** Render command type selector + sub-type + message field. */
function renderCommandFields(cmd: RelayCommand, onChange: (updated: RelayCommand) => void) {
  const subTypes = getSubTypes(cmd.kind);

  // Captured here, inside the guarded branch, so the closure below can
  // reference a definitely-defined `content` without a non-null assertion —
  // `cmd.content` itself is not narrowed inside a closure, but this local
  // `const` is.
  let wideWriteAllFields: React.ReactNode = null;
  if (subTypes && cmd.content && cmd.content.kind === "RelayWriteAllMessage") {
    const content = cmd.content;
    wideWriteAllFields = (
      <div className="col-12 mt-2">
        {renderU128Fields(
          U128_VALUE_LABEL,
          content.value ?? defaultU128(),
          (updated) =>
            onChange({
              ...cmd,
              content: {
                ...content,
                value: updated,
              },
            }),
          "numato-relay-writeall",
        )}
      </div>
    );
  }

  return (
    <>
      <div className="row">
        <div className="col-12 col-md-4 mt-2">
          <span className="generalStyles__inputLabel">Command Category</span>
          <Select
            className="generalStyles__width100"
            size="small"
            value={cmd.kind}
            onChange={(val: string) => onChange(buildDefaultCommand(val))}
            style={{ marginTop: 4 }}
          >
            {COMMAND_CATEGORIES.map((opt) => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        </div>
        {subTypes && cmd.content && (
          <div className="col-12 col-md-4 mt-2">
            <span className="generalStyles__inputLabel">Sub-Type</span>
            <Select
              className="generalStyles__width100"
              size="small"
              value={cmd.content.kind}
              onChange={(val: string) => onChange(buildDefaultCommandSubType(cmd.kind, val))}
              style={{ marginTop: 4 }}
            >
              {subTypes.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        )}
        {subTypes && cmd.content && subTypeNeedsMessage(cmd.kind, cmd.content.kind) && (
          <div className="col-12 col-md-4 mt-2">
            <span className="generalStyles__inputLabel">Message (uint8)</span>
            <InputNumber
              className="generalStyles__width100"
              size="small"
              min={0}
              max={255}
              value={cmd.content.message ?? 0}
              onChange={(v) =>
                onChange({
                  ...cmd,
                  content: {
                    ...cmd.content!,
                    message: v ?? 0,
                  },
                })
              }
              style={{ marginTop: 4 }}
            />
          </div>
        )}
        {wideWriteAllFields}
      </div>
    </>
  );
}

/** Render response type selector + type-specific fields. */
function renderResponseFields(resp: NumatoResponse, onChange: (updated: NumatoResponse) => void) {
  return (
    <>
      <div className="row">
        <div className="col-12 col-md-4 mt-2">
          <span className="generalStyles__inputLabel">Response Type</span>
          <Select
            className="generalStyles__width100"
            size="small"
            value={resp.kind}
            onChange={(val: string) => onChange(buildDefaultResponse(val))}
            style={{ marginTop: 4 }}
          >
            {RESPONSE_TYPES.map((opt) => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        </div>
        {resp.kind === "StringResponse" && (
          <div className="col-12 col-md-8 mt-2">
            <span className="generalStyles__inputLabel">Message (string)</span>
            <Input
              size="small"
              value={(resp.message as string) ?? ""}
              onChange={(e) =>
                onChange({
                  ...resp,
                  message: e.target.value,
                })
              }
              style={{ marginTop: 4 }}
            />
          </div>
        )}
        {resp.kind === "NumericResponse" && (
          <div className="col-12 col-md-4 mt-2">
            <span className="generalStyles__inputLabel">Message (uint)</span>
            <InputNumber
              className="generalStyles__width100"
              size="small"
              min={0}
              value={(resp.message as number) ?? 0}
              onChange={(v) => onChange({ ...resp, message: v ?? 0 })}
              style={{ marginTop: 4 }}
            />
          </div>
        )}
        {resp.kind === "BoolResponse" && (
          <div className="col-12 col-md-4 mt-2">
            <span className="generalStyles__inputLabel">Message (bool)</span>
            <div style={{ marginTop: 8 }}>
              <Switch
                checked={(resp.message as boolean) ?? false}
                onChange={(checked) => onChange({ ...resp, message: checked })}
              />
            </div>
          </div>
        )}
        {resp.kind === "TupleResponse" && (
          <>
            <div className="col-12 col-md-4 mt-2">
              <span className="generalStyles__inputLabel">val_0 (uint)</span>
              <InputNumber
                className="generalStyles__width100"
                size="small"
                min={0}
                value={resp.val_0 ?? 0}
                onChange={(v) => onChange({ ...resp, val_0: v ?? 0 })}
                style={{ marginTop: 4 }}
              />
            </div>
            <div className="col-12 col-md-4 mt-2">
              <span className="generalStyles__inputLabel">val_1 (uint)</span>
              <InputNumber
                className="generalStyles__width100"
                size="small"
                min={0}
                value={resp.val_1 ?? 0}
                onChange={(v) => onChange({ ...resp, val_1: v ?? 0 })}
                style={{ marginTop: 4 }}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}

/** Render the LN2 lookup value fields (bank, lift, dev_type, message type, active). */
function renderLookupValueFields(
  val: NumatoLookupValue,
  onChange: (updated: NumatoLookupValue) => void,
  bankCtx?: BankContextInfo,
) {
  return (
    <>
      <div className="row">
        {bankCtx ? (
          <>
            <div className="col-12 col-md-6 mt-2">
              <span className="generalStyles__inputLabel">Bank</span>
              <div
                style={{
                  marginTop: 4,
                  padding: "4px 8px",
                  background: "rgba(128, 128, 128, 0.15)",
                  borderRadius: 4,
                }}
              >
                {bankCtx.bankName || bankCtx.bankId}
                <span style={{ fontSize: 10, opacity: 0.7, display: "block" }}>
                  Auto-populated from bank assignment
                </span>
              </div>
            </div>
            <div className="col-12 col-md-6 mt-2">
              <span className="generalStyles__inputLabel">Lift</span>
              <Select
                className="generalStyles__width100"
                size="small"
                value={val.lift_id || undefined}
                placeholder="Select lift"
                onChange={(v: string) => {
                  onChange({
                    ...val,
                    bank_id: bankCtx.bankId,
                    lift_id: v,
                  });
                }}
                allowClear
                onClear={() =>
                  onChange({
                    ...val,
                    lift_id: "",
                  })
                }
                style={{ marginTop: 4 }}
              >
                {bankCtx.lifts.map((l) => (
                  <Select.Option key={l.id} value={l.id}>
                    {l.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </>
        ) : (
          <>
            <div className="col-12 col-md-6 mt-2">
              <span className="generalStyles__inputLabel">Bank ID</span>
              <Input
                size="small"
                value={val.bank_id}
                placeholder="UUID"
                onChange={(e) =>
                  onChange({
                    ...val,
                    bank_id: e.target.value,
                  })
                }
                style={{ marginTop: 4 }}
              />
            </div>
            <div className="col-12 col-md-6 mt-2">
              <span className="generalStyles__inputLabel">Lift ID</span>
              <Input
                size="small"
                value={val.lift_id}
                placeholder="UUID"
                onChange={(e) =>
                  onChange({
                    ...val,
                    lift_id: e.target.value,
                  })
                }
                style={{ marginTop: 4 }}
              />
              <span style={{ fontSize: 10, opacity: 0.7, display: "block" }}>
                Names are resolved for display from the bank, not stored in the CDO.
              </span>
            </div>
          </>
        )}
      </div>
      <div className="row">
        <div className="col-12 col-md-3 mt-2">
          <span className="generalStyles__inputLabel">Device Type</span>
          <Select
            className="generalStyles__width100"
            size="small"
            value={val.dev_type}
            onChange={(v: number) => onChange({ ...val, dev_type: v })}
            style={{ marginTop: 4 }}
          >
            {DEV_TYPE_OPTIONS.map((opt) => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>
      <NumatoFlatMessageFields
        msg={val.msg}
        onChange={(updatedMsg) => onChange({ ...val, msg: updatedMsg })}
      />
    </>
  );
}

/** Render the "Serial port"/"Baud rate" (USB) or "GPIO chip" (host) fields. */
function renderTriggerBackendFields(
  controller: ControllerBoard,
  onChangeController: (updated: ControllerBoard) => void,
) {
  if (controller.backend.kind === "NumatoUsbGpioPins") {
    const backend = controller.backend;
    // Mirrors controllerProblems' own two checks: empty (or whitespace-only)
    // and leading/trailing whitespace are both rejected by the server, so
    // both must show the error state here, not just the empty case (N-c).
    const trimmedPath = backend.port.path.trim();
    const pathHasError = !trimmedPath || backend.port.path !== trimmedPath;
    return (
      <div className="row">
        <div className="col-12 col-md-6 mt-2">
          <span className="generalStyles__inputLabel">Serial port</span>
          <Input
            data-testid="numato-relay-serial-port"
            size="small"
            status={pathHasError ? "error" : undefined}
            value={backend.port.path}
            onChange={(e) =>
              onChangeController({
                ...controller,
                backend: { ...backend, port: { ...backend.port, path: e.target.value } },
              })
            }
            style={{ marginTop: 4 }}
          />
        </div>
        <div className="col-12 col-md-6 mt-2">
          <span className="generalStyles__inputLabel">Baud rate</span>
          <InputNumber
            data-testid="numato-relay-baud-rate"
            className="generalStyles__width100"
            size="small"
            min={1}
            max={MAX_BAUD_RATE}
            precision={0}
            value={backend.port.baud_rate}
            onChange={(v) => {
              // antd reports null when the field is cleared — keep whatever
              // was there rather than snapping to a guessed rate.
              if (v === null) return;
              onChangeController({
                ...controller,
                backend: { ...backend, port: { ...backend.port, baud_rate: v } },
              });
            }}
            style={{ marginTop: 4 }}
          />
        </div>
      </div>
    );
  }
  const backend = controller.backend;
  return (
    <div className="row">
      <div className="col-12 col-md-6 mt-2">
        <span className="generalStyles__inputLabel">GPIO chip</span>
        <InputNumber
          data-testid="numato-relay-gpio-chip"
          className="generalStyles__width100"
          size="small"
          min={0}
          max={255}
          precision={0}
          value={backend.chip}
          onChange={(v) => {
            if (v === null) return;
            onChangeController({ ...controller, backend: { ...backend, chip: v } });
          }}
          style={{ marginTop: 4 }}
        />
      </div>
    </div>
  );
}

/** Render the per-relay trigger line list (RL0, RL1, …) with add/remove. */
function renderTriggerLines(
  controller: ControllerBoard,
  onChangeController: (updated: ControllerBoard) => void,
) {
  const values = triggerValues(controller.backend);
  const maxTrigger = triggerMax(controller.backend);
  const freeTrigger = nextFreeTrigger(controller.backend);
  // A row is in error if its own value is out of range / not a whole number,
  // or if it shares a value with another row — mirrors controllerProblems.
  const rowIsInvalid = (value: number, idx: number): boolean => {
    if (!Number.isInteger(value) || value < 0 || value > maxTrigger) return true;
    return values.some((other, otherIdx) => otherIdx !== idx && other === value);
  };
  return (
    <div style={{ marginTop: 12 }}>
      <span style={sectionLabelStyle}>Trigger Lines</span>
      {values.map((value, idx) => (
        <div className="row" key={`rl_${idx}`}>
          <div className="col-12 col-md-4 mt-2">
            <span className="generalStyles__inputLabel">{`RL${idx}`}</span>
            <InputNumber
              data-testid={`numato-relay-trigger-${idx}`}
              className="generalStyles__width100"
              size="small"
              min={0}
              max={maxTrigger}
              precision={0}
              status={rowIsInvalid(value, idx) ? "error" : undefined}
              value={value}
              onChange={(v) => {
                // antd reports null when the field is cleared — keep the
                // previous value rather than snapping to 0.
                if (v === null) return;
                onChangeController({
                  ...controller,
                  backend: withTriggerValues(
                    controller.backend,
                    values.map((existing, i) => (i === idx ? v : existing)),
                  ),
                });
              }}
              style={{ marginTop: 4 }}
            />
            <Button
              data-testid={`numato-relay-trigger-remove-${idx}`}
              type="text"
              danger
              size="small"
              icon={<FontAwesomeIcon icon={faTrash} />}
              onClick={() =>
                onChangeController({
                  ...controller,
                  backend: withTriggerValues(
                    controller.backend,
                    values.filter((_, i) => i !== idx),
                  ),
                })
              }
              style={{ marginLeft: 8 }}
            />
          </div>
        </div>
      ))}
      <Button
        data-testid="numato-relay-trigger-add"
        type="dashed"
        onClick={() => {
          if (freeTrigger === undefined) return;
          onChangeController({
            ...controller,
            backend: withTriggerValues(controller.backend, [...values, freeTrigger]),
          });
        }}
        disabled={values.length >= MAX_TRIGGER_RELAYS || freeTrigger === undefined}
        block
      >
        + Add relay
      </Button>
    </div>
  );
}

/**
 * Render the "Board" section: relay board vs. relay controller board, and
 * its fields.
 *
 * `problems` (Fix 3) is everything the wizard will block saving this relay
 * CDO on — controller-board problems, mapping-table problems that board
 * cannot serve, and wide Write All U128 problems — not just the controller
 * board's own field-level problems. Computed by the caller from the whole
 * CDO, since a single `ControllerBoard` value carries none of that context.
 */
function renderBoardSection(
  controller: ControllerBoard | undefined,
  controllerUnreadable: boolean,
  onChangeController: (updated: ControllerBoard | undefined) => void,
  problems: string[],
) {
  // A controller that hydrate() could not read leaves the Board select with
  // no selection at all, rather than defaulting to "Relay board" — the saved
  // value is gone, and defaulting silently would hide that from the operator.
  // Once either option is chosen (changeController runs), controllerUnreadable
  // is cleared and this reverts to the normal relay-board/controller-board mapping.
  let boardValue: string | undefined;
  if (controller) {
    boardValue = "controller-board";
  } else if (!controllerUnreadable) {
    boardValue = "relay-board";
  }
  return (
    <>
      <div className="row">
        <div className="col-12 col-md-6 mt-2">
          <span className="generalStyles__inputLabel">Board</span>
          <Select
            data-testid="numato-relay-board-select"
            className="generalStyles__width100"
            size="small"
            placeholder="Choose a board type"
            value={boardValue}
            onChange={(val: string) =>
              onChangeController(val === "controller-board" ? defaultControllerBoard() : undefined)
            }
            style={{ marginTop: 4 }}
          >
            {BOARD_OPTIONS.map((opt) => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
          <span style={{ fontSize: 10, opacity: 0.7, display: "block", marginTop: 4 }}>
            A controller board has no socket of its own — put its adapter on an ADAPTER channel with
            no socket address.
          </span>
        </div>
      </div>
      {controller && (
        <>
          <div className="row">
            <div className="col-12 col-md-6 mt-2">
              <span className="generalStyles__inputLabel">Trigger backend</span>
              <Select
                data-testid="numato-relay-trigger-backend-select"
                className="generalStyles__width100"
                size="small"
                value={controller.backend.kind}
                onChange={(val: string) =>
                  onChangeController({
                    ...controller,
                    backend:
                      val === "NumatoUsbGpioPins" ? defaultUsbBackend() : defaultHostBackend(),
                  })
                }
                style={{ marginTop: 4 }}
              >
                {TRIGGER_BACKEND_OPTIONS.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>
          {renderTriggerBackendFields(controller, onChangeController)}
          {renderTriggerLines(controller, onChangeController)}
          <div className="row">
            <div className="col-12 col-md-6 mt-2">
              <span className="generalStyles__inputLabel">
                Inverted trigger wiring (energize on low)
              </span>
              <div style={{ marginTop: 8 }}>
                <Switch
                  data-testid="numato-relay-inverted-wiring"
                  checked={controller.active_low}
                  onChange={(checked) => onChangeController({ ...controller, active_low: checked })}
                />
              </div>
            </div>
          </div>
          {problems.length > 0 && (
            <Alert
              type="error"
              showIcon
              message="This adapter cannot be saved yet"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {problems.map((problem) => (
                    <li key={problem}>{problem}</li>
                  ))}
                </ul>
              }
              style={{ marginTop: 12 }}
            />
          )}
        </>
      )}
    </>
  );
}

// ************************************************ */
// COMPONENT ************************************** */
export interface BankContextInfo {
  bankId: string;
  bankName: string;
  lifts: { id: string; name: string }[];
}

interface CdoNumatoRelayEditorProps {
  jsonValue: string;
  onChange: (json: string) => void;
  bankContext?: BankContextInfo;
  defaultDevType?: number;
}

/**
 * Parse a persisted CDO string into typed state, coercing every message
 * through readNumatoFlatMessage so a legacy nested-union payload (or any
 * malformed entry) hydrates to "not configured" instead of throwing. The
 * optional `controller` key is coerced the same way: anything that is not a
 * well-formed ControllerBoard hydrates as a plain relay board.
 */
function hydrate(raw: string): NumatoRelayCdo {
  try {
    const parsed = JSON.parse(raw) as Partial<NumatoRelayCdo>;
    const controller = readController((parsed as { controller?: unknown }).controller);
    return {
      response_to_message: (parsed.response_to_message ?? []).map((entry) => ({
        ...entry,
        value: {
          dev_type: entry.value?.dev_type ?? 4,
          bank_id: typeof entry.value?.bank_id === "string" ? entry.value.bank_id : "",
          lift_id: typeof entry.value?.lift_id === "string" ? entry.value.lift_id : "",
          timestamp: entry.value?.timestamp ?? 0,
          derived: entry.value?.derived ?? false,
          msg: readNumatoFlatMessage((entry.value as { msg?: unknown })?.msg),
        },
      })),
      message_to_cmd: (parsed.message_to_cmd ?? []).map((entry) => ({
        ...entry,
        key: readNumatoFlatMessage(entry.key),
      })),
      ...(controller ? { controller } : {}),
    };
  } catch {
    return buildEmptyCdo();
  }
}

/**
 * Whether the raw JSON's `controller` key is present but does not parse as a
 * well-formed ControllerBoard — the case hydrate() silently drops down to a
 * plain relay board. Kept separate from hydrate() itself: hydrate() must stay
 * defensive regardless of whether anything ever warns about the drop, and
 * this only exists to drive that warning.
 */
function controllerWasUnreadable(raw: string): boolean {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || !("controller" in parsed)) return false;
    return readController((parsed as { controller?: unknown }).controller) === undefined;
  } catch {
    return false;
  }
}

/**
 * The raw `controller` value exactly as it was on disk, whatever shape it
 * is — used only to re-emit an unreadable controller unchanged (Fix 2).
 * Undefined when the raw JSON does not parse or carries no `controller` key.
 */
function rawControllerValue(raw: string): unknown {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || !("controller" in parsed)) return undefined;
    return (parsed as { controller?: unknown }).controller;
  } catch {
    return undefined;
  }
}

export default function CdoNumatoRelayEditor({
  jsonValue,
  onChange,
  bankContext = undefined,
  defaultDevType = undefined,
}: CdoNumatoRelayEditorProps) {
  const [state, setState] = useState<NumatoRelayCdo>(() => hydrate(jsonValue));
  const [controllerUnreadable, setControllerUnreadable] = useState<boolean>(() =>
    controllerWasUnreadable(jsonValue),
  );
  // The saved `controller` value exactly as loaded, kept only while it is
  // unreadable (Fix 2) — every emission while `controllerUnreadable` is true
  // re-sends this unchanged, so editing an unrelated mapping cannot silently
  // drop it. Replaced (by clearing controllerUnreadable) only when the
  // operator picks a board type in changeController.
  const [rawUnreadableController, setRawUnreadableController] = useState<unknown>(() =>
    controllerWasUnreadable(jsonValue) ? rawControllerValue(jsonValue) : undefined,
  );
  const lastOutputRef = useRef<string>(jsonValue);

  // Sync jsonValue prop -> internal state when it changes externally
  useEffect(() => {
    if (jsonValue === lastOutputRef.current) return;
    if (!jsonValue.trim()) return;
    setState(hydrate(jsonValue));
    const unreadable = controllerWasUnreadable(jsonValue);
    setControllerUnreadable(unreadable);
    setRawUnreadableController(unreadable ? rawControllerValue(jsonValue) : undefined);
  }, [jsonValue]);

  // Emit changes whenever state updates. While the saved controller is
  // unreadable, the typed state never carries a `controller` key at all
  // (hydrate() drops it) — re-insert the untouched raw value here so an
  // unrelated mapping edit cannot lose it (Fix 2).
  //
  // `unreadable`/`raw` are taken as explicit parameters rather than read
  // from the `controllerUnreadable`/`rawUnreadableController` state inside
  // this closure: React state setters do not take effect until the next
  // render, so `changeController` (which flips both in the same call that
  // emits) must pass the post-change values itself rather than let this
  // function read the still-stale ones.
  const emitChangeWithController = (next: NumatoRelayCdo, unreadable: boolean, raw: unknown) => {
    setState(next);
    // The saved `controller` may be unreadable — anything at all, not a
    // well-formed `ControllerBoard` — so the emitted payload's `controller`
    // is typed as `unknown` here rather than narrowed to `ControllerBoard`,
    // even though the value is only ever handed to JSON.stringify below.
    const payload: Omit<NumatoRelayCdo, "controller"> & { controller?: unknown } = unreadable
      ? { ...next, controller: raw }
      : next;
    const json = JSON.stringify(payload, null, 2);
    lastOutputRef.current = json;
    onChange(json);
  };

  const emitChange = (next: NumatoRelayCdo) =>
    emitChangeWithController(next, controllerUnreadable, rawUnreadableController);

  // ************************************************ */
  // BOARD / CONTROLLER HELPERS *********************** */
  const changeController = (updated: ControllerBoard | undefined) => {
    // The warning is about the value that was on disk when this editor
    // mounted; once the user has picked a board (any board) that saved value
    // is no longer relevant to what is about to be saved.
    setControllerUnreadable(false);
    setRawUnreadableController(undefined);
    emitChangeWithController(
      updated ? { ...state, controller: updated } : withoutController(state),
      false,
      undefined,
    );
  };

  // ************************************************ */
  // RESPONSE_TO_MESSAGE HELPERS ******************** */
  const updateR2M = (
    index: number,
    updater: (e: ResponseToMessageEntry) => ResponseToMessageEntry,
  ) => {
    const updated = state.response_to_message.map((entry, i) =>
      i === index ? updater(structuredClone(entry)) : entry,
    );
    emitChange({ ...state, response_to_message: updated });
  };

  const addR2M = () => {
    const newEntry: ResponseToMessageEntry = {
      key: {
        command: buildDefaultCommand("RelayCommandContentTable"),
        response: buildDefaultResponse("NoResponse"),
      },
      value: buildDefaultLookupValue(defaultDevType ?? 4),
    };
    emitChange({
      ...state,
      response_to_message: [...state.response_to_message, newEntry],
    });
  };

  const removeR2M = (index: number) => {
    emitChange({
      ...state,
      response_to_message: state.response_to_message.filter((_, i) => i !== index),
    });
  };

  // ************************************************ */
  // MESSAGE_TO_CMD HELPERS ************************* */
  const updateM2C = (index: number, updater: (e: MessageToCmdEntry) => MessageToCmdEntry) => {
    const updated = state.message_to_cmd.map((entry, i) =>
      i === index ? updater(structuredClone(entry)) : entry,
    );
    emitChange({ ...state, message_to_cmd: updated });
  };

  const addM2C = () => {
    const newEntry: MessageToCmdEntry = {
      key: buildDefaultNumatoFlatMessage(),
      value: buildDefaultCommand("RelayCommandContentTable"),
    };
    emitChange({
      ...state,
      message_to_cmd: [...state.message_to_cmd, newEntry],
    });
  };

  const removeM2C = (index: number) => {
    emitChange({
      ...state,
      message_to_cmd: state.message_to_cmd.filter((_, i) => i !== index),
    });
  };

  // Fix 3: everything the wizard will block saving this relay CDO on for its
  // controller board — controller-field problems, mapping-table commands the
  // board cannot serve, and wide Write All U128 problems — deduplicated
  // (numatoRelayControllerProblems already omits a U128 half's own format
  // problem, so the two lists do not overlap in practice, but Set guards it
  // regardless). Only meaningful once a controller board is chosen, so it is
  // skipped while unreadable — that state has its own warning Alert below.
  const boardAlertProblems = state.controller
    ? Array.from(
        new Set([...numatoRelayControllerProblems(state), ...numatoU128Problems(state, "relay")]),
      )
    : [];

  // ************************************************ */
  // RENDER ***************************************** */
  return (
    <div className="container">
      {/* ============================================= */}
      {/* BOARD */}
      <Divider orientation="left" plain>
        Board
      </Divider>
      {renderBoardSection(
        state.controller,
        controllerUnreadable,
        changeController,
        boardAlertProblems,
      )}

      {controllerUnreadable && (
        <Alert
          type="warning"
          showIcon
          message="The saved controller board configuration could not be read. Choose a board type to replace it; the adapter cannot be saved until you do."
          style={{ marginTop: 12, marginBottom: 12 }}
        />
      )}

      {/* ============================================= */}
      {/* RESPONSE TO MESSAGE MAPPINGS */}
      <Divider orientation="left" plain>
        Response to Message Mappings ({state.response_to_message.length})
      </Divider>
      {state.response_to_message.map((entry, idx) => (
        <div key={`r2m_${idx}`} style={entryStyle}>
          <Button
            type="text"
            danger
            size="small"
            style={deleteButtonStyle}
            icon={<FontAwesomeIcon icon={faTrash} />}
            onClick={() => removeR2M(idx)}
          />
          <strong style={{ display: "block", marginBottom: 8 }}>Mapping #{idx + 1}</strong>

          {/* Command */}
          <span style={sectionLabelStyle}>Command</span>
          {renderCommandFields(entry.key.command, (updatedCmd) =>
            updateR2M(idx, (e) => ({
              ...e,
              key: { ...e.key, command: updatedCmd },
            })),
          )}

          {/* Response */}
          <div style={{ marginTop: 12 }}>
            <span style={sectionLabelStyle}>Response</span>
            {renderResponseFields(entry.key.response, (updatedResp) =>
              updateR2M(idx, (e) => ({
                ...e,
                key: {
                  ...e.key,
                  response: updatedResp,
                },
              })),
            )}
          </div>

          {/* LN2 Lookup Value */}
          <div style={{ marginTop: 12 }}>
            <span style={sectionLabelStyle}>LN2 Lookup Data</span>
            {renderLookupValueFields(
              entry.value,
              (updatedVal) =>
                updateR2M(idx, (e) => ({
                  ...e,
                  value: updatedVal,
                })),
              bankContext,
            )}
          </div>
        </div>
      ))}
      <Button type="dashed" onClick={addR2M} block>
        + Add Response to Message Mapping
      </Button>

      {/* ============================================= */}
      {/* MESSAGE TO COMMAND MAPPINGS */}
      <Divider orientation="left" plain>
        Message to Command Mappings ({state.message_to_cmd.length})
      </Divider>
      {state.message_to_cmd.map((entry, idx) => (
        <div key={`m2c_${idx}`} style={entryStyle}>
          <Button
            type="text"
            danger
            size="small"
            style={deleteButtonStyle}
            icon={<FontAwesomeIcon icon={faTrash} />}
            onClick={() => removeM2C(idx)}
          />
          <strong style={{ display: "block", marginBottom: 8 }}>Mapping #{idx + 1}</strong>

          {/* LN2 Message Key */}
          <span style={sectionLabelStyle}>LN2 Message</span>
          <NumatoFlatMessageFields
            msg={entry.key}
            onChange={(updatedMsg) => updateM2C(idx, (e) => ({ ...e, key: updatedMsg }))}
          />

          {/* Command Value */}
          <div style={{ marginTop: 12 }}>
            <span style={sectionLabelStyle}>Command</span>
            {renderCommandFields(entry.value, (updatedCmd) =>
              updateM2C(idx, (e) => ({
                ...e,
                value: updatedCmd,
              })),
            )}
          </div>
        </div>
      ))}
      <Button type="dashed" onClick={addM2C} block>
        + Add Message to Command Mapping
      </Button>
    </div>
  );
}
